import { useCallback, useEffect, useState } from 'react';
import Header from '../components/Header';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Key } from 'lucide-react';
import { assignSubscriptionToUser, verifySignature, createSubscription, cancelCreateSubscription, getPlanDescriptions } from '../utils/subscriptionApi';
import { logout } from '../utils/authApi';
import Input from '../components/elements/Input';
import Loading from '../components/elements/Loading';
import Button from '../components/elements/Button';
import ConfirmModal from '../components/ConfirmModal';
import Info from '../components/elements/Info';
import { acquireRazorpayScript } from '../utils/razorpayScript';

export default function SubscriptionPage() {
    const navigate = useNavigate();
    const isLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true';
    const [email] = useState(sessionStorage.getItem('email'));
    const [pageReady, setPageReady] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [subscriptionError, setSubscriptionError] = useState(null);
    const [razorpayLoaded, setRazorpayLoaded] = useState(false);
    const [failedVerification, setFailedVerification] = useState(null); // { paymentId, signature, subscriptionId }
    const [manualSubscriptionId, setManualSubscriptionId] = useState('');
    const [manualIdMessage, setManualIdMessage] = useState('');
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    const allPlans = getPlanDescriptions();

    useEffect(() => {
        document.title = "Subscribe | Eternal Locker™";
        if (!isLoggedIn) {
            navigate('/');
            return;
        } else {
            setPageReady(true);
        }

    }, [isLoggedIn, navigate]);

    // Load Razorpay script
    const loadRazorpayScript = useCallback(() => {
        setRazorpayLoaded(false);
        return acquireRazorpayScript({
            onLoaded: () => setRazorpayLoaded(true),
            onError: () => setRazorpayLoaded(false)
        });
    }, []);

    useEffect(() => {
        const releaseScript = loadRazorpayScript();
        return () => {
            setRazorpayLoaded(false);
            if (typeof releaseScript === 'function') {
                releaseScript();
            }
        };
    }, [loadRazorpayScript]);

    const handleManualSubscriptionId = () => {
        if (!manualSubscriptionId) {
            setManualIdMessage('Please enter a subscription ID.');
            return;
        }
        assignSubscriptionToUser(manualSubscriptionId).then(response => {
            if (response.success) {
                setShowSuccessModal(true);
            } else {
                setManualIdMessage('Invalid subscription Key.');
            }
        }).catch(() => {
            setManualIdMessage('Error verifying subscription Key. Please try again later.');
        });
    };

    const handleSubscription = useCallback(async (type) => {
        setIsLoading(true);
        setSubscriptionError(null);
        if (!isLoggedIn) {
            setSubscriptionError('User not authenticated. Please log in again.');
            setIsLoading(false);
            return;
        }

        if (type === 'FREE') {
            // Directly assign free subscription without payment
            try {
                const response = await assignSubscriptionToUser('FREE');
                if (response.success) {
                    setShowSuccessModal(true);
                } else {
                    setSubscriptionError('Could not assign free subscription. Please try again.');
                }
            } catch (error) {
                setSubscriptionError('Error assigning free subscription. Please try again.');
            }

            setIsLoading(false);
            return;
        }

        let response;
        response = await createSubscription(type);
        if (!response.success) {
            setSubscriptionError('Could not initialize subscription. Please try again.');
            setIsLoading(false);
            return;
        }

        const { subscriptionId, key } = response;
        if (!subscriptionId || !key) {
            setSubscriptionError('Payment gateway configuration is invalid. Please contact support.');
            setIsLoading(false);
            return;
        }
        const options = {
            key,
            subscription_id: subscriptionId,
            name: "Eternal Locker™",
            image: `${window.location.origin}/logo512WithBackground.png`,
            description: `Subscribe to ${type} Plan`,
            handler: async function (resp) {
                // Verify Razorpay signature with backend
                try {
                    const verifyRes = await verifySignature(resp.razorpay_payment_id, resp.razorpay_signature);
                    if (verifyRes.success) {
                        setShowSuccessModal(true);
                    } else {
                        sessionStorage.removeItem('subscriptionId');
                        setFailedVerification({
                            paymentId: resp.razorpay_payment_id,
                            signature: resp.razorpay_signature,
                            subscriptionId: subscriptionId
                        });
                        setSubscriptionError('Payment signature verification failed. Please contact support or retry verification.');
                    }
                } catch (err) {
                    setFailedVerification({
                        paymentId: resp.razorpay_payment_id,
                        signature: resp.razorpay_signature,
                        subscriptionId: subscriptionId
                    });
                    setSubscriptionError('Error verifying payment. Please contact support or retry verification.');
                }
            },
            prefill: { email: email },
            readonly: { email: true },
            hidden: { contact: true },
            theme: { color: '#000000' },
            modal: {
                escape: false,
                backdropclose: false,
                ondismiss: async function() {
                    await cancelCreateSubscription();
                    setIsLoading(false);
                }
            }
        };
        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', function (resp) {
            setSubscriptionError(`Payment failed: ${resp.error.description}`);
            setIsLoading(false);
        });
        rzp.on('subscription.failed', function (resp) {
            setSubscriptionError(`Subscription failed: ${resp.error.description}`);
            setIsLoading(false);
        });
        rzp.open();
        setIsLoading(false);
    }, []);

    // Retry verification handler
    const handleRetryVerification = async () => {
        if (!failedVerification || !isLoggedIn) return;
        setIsLoading(true);
        setSubscriptionError(null);
        try {
            const verifyRes = await verifySignature(failedVerification.paymentId, failedVerification.signature);
            if (verifyRes.success) {
                setShowSuccessModal(true);
            } else {
                setSubscriptionError('Payment signature verification failed again. Please contact support.');
            }
        } catch (err) {
            setSubscriptionError('Error verifying payment. Please contact support.');
        }
        setIsLoading(false);
    };

    if (!pageReady) {
        return null;
    }

    return (
        <>
            <Header type="small" />
            <div className="lg:order-2 flex justify-center w-full">
                <div className="w-full max-w-7xl">
                    <div className="w-full p-8 space-y-6 bg-card-background shadow-2xl rounded-xl">
                        <h2 className="text-3xl font-extrabold text-center text-accent-main">Choose Your Subscription</h2>
                        <div className="text-center space-y-4">
                            <Key className="w-12 h-12 mx-auto text-accent-main animate-pulse" />
                            <p className="text-lg text-light mb-4 pb-4">Unlock your Eternal Locker™ and enjoy peace of mind with secure, perpetual storage for your most critical data.</p>
                            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mt-4">
                                {/* Free Plan Card */}
                                <div className="bg-gray-50 border-2 border-gray-300 rounded-xl p-6 flex flex-col items-center shadow hover:scale-105 transition-transform duration-200">
                                    <div className="text-2xl font-bold text-gray-700 mb-2">Free</div>
                                    <div className="text-3xl font-extrabold text-gray-900 mb-1">₹0</div>
                                    <ul className="text-left text-sm text-gray-800 mb-4 list-disc list-inside flex-grow">
                                        {allPlans['FREE'].features.map((feature, index) => (
                                            <li key={index}>{feature}</li>
                                        ))}
                                        <li>Perfect to start</li>
                                    </ul>
                                    <Button
                                        size='large'
                                        onClick={() => handleSubscription('FREE')}
                                        disabled={isLoading || !razorpayLoaded}
                                        className="w-full bg-gray-600 border border-gray-600 hover:bg-gray-700"
                                    >
                                        Get Started Free
                                    </Button>
                                </div>
                                {/* Silver Plan Card */}
                                <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-6 flex flex-col items-center shadow hover:scale-105 transition-transform duration-200">
                                    <div className="text-2xl font-bold text-blue-700 mb-2">Silver</div>
                                    <div className="text-3xl font-extrabold text-blue-900 mb-1">₹999 <span className="text-base font-medium">/ year</span></div>
                                    <ul className="text-left text-sm text-blue-800 mb-4 list-disc list-inside flex-grow">
                                        {allPlans['SILVER'].features.map((feature, index) => (
                                            <li key={index}>{feature}</li>
                                        ))}
                                    </ul>
                                    <Button
                                        size='large'
                                        onClick={() => handleSubscription('SILVER')}
                                        disabled={isLoading || !razorpayLoaded}
                                        className="w-full bg-blue-600 border border-blue-600 hover:bg-blue-700"
                                    >
                                        Start Silver
                                    </Button>
                                </div>
                                {/* Gold Plan Card */}
                                <div className="bg-yellow-50 border-2 border-yellow-400 rounded-xl p-6 flex flex-col items-center shadow hover:scale-105 transition-transform duration-200 relative">
                                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-orange-400 text-orange-900 rounded-full text-xs font-bold shadow">Popular</div>
                                    <div className="text-2xl font-bold text-yellow-700 mb-2">Gold</div>
                                    <div className="text-3xl font-extrabold text-yellow-900 mb-1">₹1499 <span className="text-base font-medium">/ year</span></div>
                                    <ul className="text-left text-sm text-yellow-800 mb-4 list-disc list-inside flex-grow">
                                        {allPlans['GOLD'].features.map((feature, index) => (
                                            <li key={index}>{feature}</li>
                                        ))}
                                    </ul>
                                    <Button
                                        size='large'
                                        onClick={() => handleSubscription('GOLD')}
                                        disabled={isLoading || !razorpayLoaded}
                                        className="w-full bg-yellow-600 border border-yellow-600 hover:bg-yellow-700"
                                    >
                                        Start Gold
                                    </Button>
                                </div>
                                {/* Platinum Plan Card */}
                                <div className="bg-purple-50 border-2 border-purple-300 rounded-xl p-6 flex flex-col items-center shadow hover:scale-105 transition-transform duration-200 relative">
                                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-purple-400 text-purple-900 rounded-full text-xs font-bold shadow">Best Value</div>
                                    <div className="text-2xl font-bold text-purple-700 mb-2">Platinum</div>
                                    <div className="text-3xl font-extrabold text-purple-900 mb-1">₹2499 <span className="text-base font-medium">/ year</span></div>
                                    <ul className="text-left text-sm text-purple-800 mb-4 list-disc list-inside flex-grow">
                                        {allPlans['PLATINUM'].features.map((feature, index) => (
                                            <li key={index}>{feature}</li>
                                        ))}
                                    </ul>
                                    <Button
                                        size='large'
                                        onClick={() => handleSubscription('PLATINUM')}
                                        disabled={isLoading || !razorpayLoaded}
                                        className="w-full bg-purple-600 border border-purple-600 hover:bg-purple-700"
                                    >
                                        Start Platinum
                                    </Button>
                                </div>
                            </div>
                            <div className="mb-6 p-4 bg-primary-light border border-blue-200 rounded-lg flex flex-col items-center">
                                <div className="font-semibold text-accent-main mb-2">Already have a Subscription Key?</div>
                                <div className="flex flex-row gap-2 w-full max-w-xl">
                                    <Input
                                        label="Subscription Key"
                                        value={manualSubscriptionId}
                                        onChange={e => setManualSubscriptionId(e.target.value)}
                                        placeholder="Enter your subscription key"
                                        className="flex-1"
                                        error={manualIdMessage}
                                        autoFocus
                                    />
                                    <Button type="primary" onClick={handleManualSubscriptionId} className="ml-2 mt-5 mb-3">Save</Button>
                                </div>
                            </div>
                            <div className="mt-6 text-xs text-light text-center">
                                <span className="font-semibold text-accent-main">100% Secure:</span> Your data is encrypted and protected by industry-leading security standards. Cancel anytime, no questions asked.
                            </div>
                        </div>
                        {subscriptionError && (
                            <div className="flex flex-col items-center p-3 gap-2">
                                <Info type="error">
                                    {subscriptionError}
                                </Info>
                                {failedVerification && (
                                    <Button
                                        onClick={handleRetryVerification}
                                        disabled={isLoading}
                                    >
                                        Retry Verification
                                    </Button>
                                )}
                            </div>
                        )}
                        {!razorpayLoaded && (
                            <div className="flex flex-col items-center justify-center py-6">
                                <Loading />
                                <div className="text-blue-700 font-semibold mb-2">Loading payment gateway...</div>
                                <Button
                                    onClick={loadRazorpayScript}
                                >
                                    Retry Loading
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <ConfirmModal
                open={showSuccessModal}
                title="Subscription Successful!"
                message="Your subscription has been activated successfully. Please log in again to continue."
                confirmText="Okay"
                onConfirm={async () => {
                    await logout();
                    sessionStorage.clear();
                    navigate('/');
                }}
            />
        </>
    );
}

